import type { Schema } from 'yup';
import { ValidationError } from 'yup';

import type { CustomFieldError, CustomFieldErrors } from './FormValidationError';
import { get, isValidError, set } from './ErrorsService';

const setError = (
    errors: CustomFieldErrors,
    path: string,
    error: CustomFieldError,
): CustomFieldErrors => {
    set(errors, `${path}.type`, error.type);
    set(errors, `${path}.message`, error.message);

    return errors;
};

function getMergedErrors(root: CustomFieldErrors | CustomFieldError): CustomFieldErrors {
    return Object.keys(root).length
        ? {
              root,
          }
        : {};
}

/**
 * yupValidatorResolver has to return errors in CustomFieldErrors structure (nested objects - see ErrorsService tests)
 * because formState of react-hook-form use it internally. It's important especially for forms with nested fields
 *
 * Errors are transformed into a flat dot path notation in useFormContext hook for the following use
 * */
export const yupValidationResolver =
    (validationSchema: Schema<any>) =>
    async (
        data: unknown,
        context?: unknown,
        { root = {} }: CustomFieldErrors = {},
    ): Promise<{ errors: CustomFieldErrors; values: unknown }> => {
        try {
            // transformed values from yup -> transforms types
            const values = await validationSchema.validate(data, {
                abortEarly: false,
                context: context as any,
            });

            return {
                values,
                errors: getMergedErrors(root),
            };
        } catch (errors: unknown) {
            if (!(errors instanceof ValidationError)) {
                throw errors;
            }

            const errorsTyped = errors as ValidationError;

            const mappedErrors = (errorsTyped.inner ?? []).reduce(
                (allErrors, currentError) => {
                    const path = currentError?.path || '';
                    const error = get(allErrors, path) as unknown as CustomFieldError;

                    // we already have first validation error for this path
                    if (isValidError(error)) {
                        return allErrors;
                    }

                    setError(allErrors, path, {
                        type: currentError.type ?? 'validation',
                        message: currentError.message,
                    });

                    return allErrors;
                },
                getMergedErrors(root) as CustomFieldErrors,
            );

            return {
                values: {},
                errors: mappedErrors,
            };
        }
    };
